import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import Button from '../Button';
import Input from '../Input';
import TenantService, { BulkActionResult } from '../../services/tenant.service';

interface SelectableTenant {
  id: number;
  tenant_name: string;
  status_code: string | null;
  status_name: string | null;
}

interface BulkSuspendModalProps {
  open: boolean;
  onClose: () => void;
  selectedTenants: SelectableTenant[];
  selectAllFilter: boolean;
  filterParams?: Record<string, unknown>;
  matchingCount?: number;
}

/** US-052 AC3: confirmation lists selected tenants, flags already-suspended ones as "will be skipped", requires a shared reason. */
const BulkSuspendModal: React.FC<BulkSuspendModalProps> = ({
  open,
  onClose,
  selectedTenants,
  selectAllFilter,
  filterParams,
  matchingCount,
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkActionResult | null>(null);

  const willBeSkipped = useMemo(
    () => selectedTenants.filter((tenant) => tenant.status_code !== 'active'),
    [selectedTenants]
  );

  const handleClose = useCallback(() => {
    setReason('');
    setResult(null);
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      const response: any = await TenantService.bulkSuspend({
        tenantIds: selectAllFilter ? undefined : selectedTenants.map((tenant) => tenant.id),
        filterParams: selectAllFilter ? filterParams : undefined,
        reason: reason.trim(),
      });
      setResult(response?.data ?? null);
    } finally {
      setSubmitting(false);
    }
  }, [reason, selectAllFilter, selectedTenants, filterParams]);

  const handleDownloadCsv = useCallback(() => {
    if (!result) return;
    const rows = [
      ['Tenant ID', 'Outcome', 'Reason'],
      ...result.succeeded.map((id) => [String(id), 'Succeeded', '']),
      ...result.skipped.map((row) => [String(row.id), 'Skipped', row.reason]),
      ...result.failed.map((row) => [String(row.id), 'Failed', row.reason]),
    ];
    const csv = rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `bulk-suspend-${result.bulkActionId}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }, [result]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('tenants.list.pageTexts.bulk.suspendModal.title')}</DialogTitle>
      <DialogContent>
        {!result ? (
          <>
            {selectAllFilter ? (
              <p>{t('tenants.list.pageTexts.bulk.selectAllFilter', { count: matchingCount ?? 0 })}</p>
            ) : (
              <ul className="list-unstyled mb-3">
                {selectedTenants.map((tenant) => (
                  <li key={tenant.id} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                    <span>{tenant.tenant_name}</span>
                    {tenant.status_code !== 'active' && (
                      <Chip
                        size="small"
                        color="warning"
                        label={t('tenants.list.pageTexts.bulk.suspendModal.willBeSkipped', {
                          status: tenant.status_name ?? tenant.status_code,
                        })}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
            {!selectAllFilter && willBeSkipped.length > 0 && (
              <p className="text-muted small">
                {t('tenants.list.pageTexts.bulk.suspendModal.willBeSkipped', { status: willBeSkipped.length })}
              </p>
            )}
            <Input
              id="bulk-suspend-reason"
              name="reason"
              label={t('tenants.list.pageTexts.bulk.suspendModal.reasonLabel')}
              value={reason}
              required
              onChange={(e: any) => setReason(e.target.value)}
            />
          </>
        ) : (
          <div>
            <h6>{t('tenants.list.pageTexts.bulk.results.title')}</h6>
            <div className="d-flex gap-2 mb-3">
              <Chip color="success" label={t('tenants.list.pageTexts.bulk.results.succeeded', { count: result.succeeded.length })} />
              <Chip color="warning" label={t('tenants.list.pageTexts.bulk.results.skipped', { count: result.skipped.length })} />
              <Chip color="error" label={t('tenants.list.pageTexts.bulk.results.failed', { count: result.failed.length })} />
            </div>
            <Button
              className="btn btn-outline-secondary"
              text={t('tenants.list.pageTexts.bulk.results.downloadCsv')}
              onClick={handleDownloadCsv}
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {!result ? (
          <>
            <Button
              className="btn btn-outline-secondary"
              text={t('tenants.list.pageTexts.bulk.suspendModal.cancel')}
              onClick={handleClose}
            />
            <Button
              className="btn btn-danger"
              text={t('tenants.list.pageTexts.bulk.suspendModal.confirm')}
              loading={submitting}
              disabled={!reason.trim()}
              onClick={handleConfirm}
            />
          </>
        ) : (
          <Button className="btn btn-primary" text={t('tenants.list.pageTexts.bulk.results.close')} onClick={handleClose} />
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkSuspendModal;
